/**
 * Resolving a watermark's artwork, whichever shape its row is in (VE-24693 §K).
 *
 * Since §K an image definition keeps only a small `previewDataUrl` inline and
 * puts the working raster and the original upload on its own TDO. Three shapes
 * therefore exist in the wild, and every consumer has to cope with all of them:
 *
 *   1. `imageTdoId` set, nothing inline  — the normal case.
 *   2. `imageBaseDataUrl` set inline     — written while the
 *      {@link STORE_WATERMARK_RASTER_IN_SDO} revert switch is on, and by rows
 *      that predate §K.
 *   3. neither                           — a broken or half-written row.
 *
 * Resolving that in each of the five places that draw or apply a watermark is
 * how the three shapes turn into three different behaviours, so it is resolved
 * here instead. Inline WINS when it is present AND survives the vet: it is
 * already in memory, and a row that has one does not need a round trip to say
 * what it looks like. It is vetted rather than trusted because the apply path
 * reads rows that never passed through `vetRecord` — see
 * {@link resolveWatermarkRaster}.
 *
 * Preview is separate from raster on purpose. A list surface wants the small
 * inline image and must never trigger a fetch — the settings table renders
 * straight from the store, and a fetch per row would be a request storm on a
 * page that exists to show a list. Only applying a watermark needs the full
 * raster, and only then is a fetch acceptable.
 */
import {
  serviceLoadWatermarkAsset,
  WatermarkAssetConfig,
  WATERMARK_RASTER_ASSET_TYPE,
  WATERMARK_SOURCE_IMAGE_ASSET_TYPE,
} from './services/imageAssets';
import { sanitizeStoredImageDataUrl } from '@redact-modules/documents/watermarkImage';
import { WatermarkDefinitionData } from './models';

/**
 * What a list surface should draw for an image definition, WITHOUT fetching.
 *
 * Falls back to the inline raster for a row that predates the preview field, so
 * an older definition still shows artwork rather than an empty cell.
 */
export function watermarkPreviewSrc(
  data: Pick<WatermarkDefinitionData, 'previewDataUrl' | 'imageBaseDataUrl'>
): string | undefined {
  return data.previewDataUrl ?? data.imageBaseDataUrl;
}

/**
 * The full-resolution raster, fetching from the watermark's TDO if that is
 * where it lives. Null when the definition has no artwork to resolve.
 *
 * BOTH shapes are vetted here, and that is the point of the function. The
 * fetched asset hangs off an org-writable TDO and the inline value sits in an
 * org-writable SDO, so neither one's bytes are more trustworthy than the
 * other's. The client re-encodes every upload through
 * `canvas.toDataURL('image/png')` on the way IN, which constrains what THIS app
 * writes — it says nothing about what is in a row coming back out, and anyone
 * in the org can write one through the public API. `sanitizeStoredImageDataUrl`
 * is what makes "it is a PNG we wrote" true rather than assumed.
 *
 * `vetRecord` already applies it to rows on their way into the store, but the
 * apply path does not read them from there: `findRecord` hands back the UNVETTED
 * raw record on purpose, so that enabling a row whose image this client rejects
 * does not write the rejection back to the server. That choice is why the vet
 * has to happen here, on the read, instead.
 *
 * A rejected inline value falls through to the TDO rather than failing: the
 * asset is the authoritative copy, and the inline one is a convenience.
 *
 * The pixel-dimension check is the bound that matters — a uniform 20000x20000
 * PNG is a few KB on the wire and ~1.6 GB decoded, and this value goes straight
 * into an `<img src>` and into the burn-in worker's decoder.
 */
export async function resolveWatermarkRaster(
  data: Pick<WatermarkDefinitionData, 'imageBaseDataUrl' | 'imageTdoId'>,
  cfg: WatermarkAssetConfig
): Promise<string | null> {
  const inline = sanitizeStoredImageDataUrl(data.imageBaseDataUrl);
  if (inline) {
    return inline;
  }
  if (!data.imageTdoId) {
    return null;
  }
  const fetched = await serviceLoadWatermarkAsset(
    data.imageTdoId,
    WATERMARK_RASTER_ASSET_TYPE,
    cfg
  );
  return sanitizeStoredImageDataUrl(fetched);
}

/**
 * A definition with its raster in hand, ready for `appliedFromDefinition`.
 *
 * The one place that turns "a row in the library" into "something that can be
 * drawn on a page", so the editor's picker and the folder view's batch apply
 * cannot disagree about where artwork comes from — they already disagreed once
 * about defaults, which is why `appliedFromDefinition` is a single function.
 *
 * A text definition passes straight through. An image one always goes through
 * {@link resolveWatermarkRaster}, INCLUDING when it already carries a raster
 * inline: this runs on the apply path, which reads the unvetted raw record (see
 * that function's note), so short-circuiting on a truthy inline value would hand
 * an unvetted string to `appliedFromDefinition` and from there to an `<img src>`.
 * Skipping the call saved a round trip that the inline case never makes anyway.
 *
 * A definition whose artwork does not survive the vet comes back WITHOUT it,
 * rather than keeping the value that was rejected — callers test
 * `!data.imageBaseDataUrl` to decide whether the watermark can be applied at
 * all, and leaving the bad value in place would answer that question wrong.
 */
export async function hydrateDefinitionArtwork(
  data: WatermarkDefinitionData,
  cfg: WatermarkAssetConfig
): Promise<WatermarkDefinitionData> {
  if (data.type !== 'image') {
    return data;
  }
  const raster = await resolveWatermarkRaster(data, cfg);
  return { ...data, imageBaseDataUrl: raster ?? undefined };
}

/**
 * What Download hands back: the upload's own bytes when they exist, and the
 * raster otherwise.
 *
 * The fallback is not a degradation to paper over — it is the correct answer
 * for an SVG upload, whose source is deliberately never stored (SEC-R2), and
 * for a row whose TDO holds no source asset.
 *
 * NOT vetted through `sanitizeStoredImageDataUrl`: that one accepts PNG only,
 * and the whole point of the source is that it keeps the type it was uploaded
 * as. `serviceLoadWatermarkAsset` restricts the media type on the way in, and
 * this value is written to a download, never rendered.
 */
export async function resolveWatermarkSource(
  data: Pick<WatermarkDefinitionData, 'imageBaseDataUrl' | 'imageTdoId'>,
  cfg: WatermarkAssetConfig
): Promise<string | null> {
  if (data.imageTdoId) {
    const fetched = await serviceLoadWatermarkAsset(
      data.imageTdoId,
      WATERMARK_SOURCE_IMAGE_ASSET_TYPE,
      cfg
    );
    if (fetched) {
      return fetched;
    }
  }
  return await resolveWatermarkRaster(data, cfg);
}
